import {
  defaultCoordinates,
  DndContext,
  MouseSensor,
  Translate,
  useDraggable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { DragMoveEvent } from "@dnd-kit/core/dist/types";
import { restrictToWindowEdges } from "@dnd-kit/modifiers";
import {
  Dialog,
  DialogActions,
  DialogActionsProps,
  DialogContent,
  DialogContentProps,
  DialogProps,
  DialogTitle as MuiDialogTitle,
  DialogTitleProps,
  Grid,
  Paper,
  PaperProps,
  Typography,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import React, { useCallback, useMemo, useState } from "react";

const useStyles = makeStyles(() => ({
  root: {
    maxHeight: "95%",
  },
  dialogContainer: {
    display: "block",
    width: "100%",
    height: "100%",
  },
  dialogContent: {
    display: "flex",
    flexDirection: "column",
  },
  contentContainer: {
    display: "flex",
    maxHeight: "80%",
  },
}));

type WrapperProps = PaperProps & {
  normalizedTitle: string;
};

const DialogPaper = ({ normalizedTitle, children, ...rest }: WrapperProps) => {
  const { attributes, setNodeRef } = useDraggable({
    id: normalizedTitle,
  });
  return (
    <Paper
      ref={setNodeRef}
      {...attributes}
      style={{
        transform: `translate3d(var(--${normalizedTitle}-translate-x, 0), var(--${normalizedTitle}-translate-y, 0), 0)`,
        pointerEvents: "auto",
        overflow: "hidden",
      }}
      {...rest}
    >
      {children}
    </Paper>
  );
};

type CustomDialogTitleProps = {
  title: string;
  normalizedTitle: string;
  toolbar?: JSX.Element;
  titleProps?: DialogTitleProps;
} & DialogTitleProps;

const DialogTitle = ({
  title,
  toolbar,
  normalizedTitle,
  ...titleProps
}: CustomDialogTitleProps) => {
  const { attributes, listeners, isDragging } = useDraggable({
    id: normalizedTitle,
  });
  return (
    <MuiDialogTitle
      {...titleProps}
      {...listeners}
      {...attributes}
      style={{ cursor: isDragging ? "grabbing" : "grab" }}
    >
      <Grid container alignItems="center">
        <Grid item>
          <Typography variant="h6">{title}</Typography>
        </Grid>
        {toolbar && toolbar}
      </Grid>
    </MuiDialogTitle>
  );
};

type DraggableDialogProps = {
  children: JSX.Element;
  actions?: JSX.Element;
} & DialogProps &
  Omit<CustomDialogTitleProps, "normalizedTitle"> & {
    contentProps?: DialogContentProps;
  } & {
    actionProps?: DialogActionsProps;
  };

const DraggableDialog = ({
  title,
  actions,
  toolbar,
  children,
  titleProps,
  contentProps,
  actionProps,
  ...dialogProps
}: DraggableDialogProps): JSX.Element => {
  const classes = useStyles();
  const [{ translate }, setTranslate] = useState<{
    initialTranslate: Translate;
    translate: Translate;
  }>({ initialTranslate: defaultCoordinates, translate: defaultCoordinates });
  const [initialWindowScroll, setInitialWindowScroll] =
    useState(defaultCoordinates);
  const mouseSensor = useSensor(MouseSensor);
  const sensors = useSensors(mouseSensor);
  const maxWidth = dialogProps.maxWidth ?? "lg";
  const normalizedTitle = useMemo(
    () => title.replace(/[^a-zA-Z0-9]/g, "_") ?? "unknown-dialog",
    [title],
  );

  const handleDragStart = useCallback(() => {
    setInitialWindowScroll({
      x: window.scrollX,
      y: window.scrollY,
    });
  }, []);

  const handleDragMove = useCallback(
    (e: DragMoveEvent) => {
      setTranslate(({ initialTranslate }) => ({
        initialTranslate,
        translate: {
          x: initialTranslate.x + e.delta.x - initialWindowScroll.x,
          y: initialTranslate.y + e.delta.y - initialWindowScroll.y,
        },
      }));
    },
    [initialWindowScroll.x, initialWindowScroll.y],
  );

  const handleDragEnd = useCallback(() => {
    setTranslate(({ translate: prevTranslate }) => {
      return {
        translate: prevTranslate,
        initialTranslate: prevTranslate,
      };
    });
    setInitialWindowScroll(defaultCoordinates);
  }, []);

  const handleDragCancel = useCallback(() => {
    setTranslate(({ initialTranslate }) => ({
      translate: initialTranslate,
      initialTranslate,
    }));
    setInitialWindowScroll(defaultCoordinates);
  }, []);

  const RenderPaperWithProps = useCallback(
    (props: React.PropsWithChildren<PaperProps>) => {
      return (
        <DialogPaper
          {...props}
          normalizedTitle={normalizedTitle}
          sx={{ outline: "none" }}
        />
      );
    },
    [normalizedTitle],
  );

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
      modifiers={[restrictToWindowEdges]}
    >
      <Dialog
        classes={{ paper: classes.root }}
        fullWidth
        maxWidth={maxWidth}
        PaperComponent={RenderPaperWithProps}
        hideBackdrop
        disablePortal
        keepMounted
        {...dialogProps}
        style={
          {
            [`--${normalizedTitle}-translate-x`]: `${translate?.x ?? 0}px`,
            [`--${normalizedTitle}-translate-y`]: `${translate?.y ?? 0}px`,
            pointerEvents: "none",
          } as React.CSSProperties
        }
      >
        <DialogTitle
          title={title}
          normalizedTitle={normalizedTitle}
          toolbar={toolbar}
          sx={{ outline: "none" }}
          {...titleProps}
        />
        <DialogContent
          className={classes.dialogContent}
          dividers
          {...contentProps}
        >
          {children}
        </DialogContent>
        {actions && <DialogActions {...actionProps}>{actions}</DialogActions>}
      </Dialog>
    </DndContext>
  );
};

export default DraggableDialog;
